import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import ClientPortalLayout from "./ClientPortalLayout";
import { Grid, Paper, Typography, Box, TextField, Button, Switch, FormControlLabel, Divider, Card, CardContent, Alert, CircularProgress } from "@mui/material";
import { Settings as SettingsIcon, Notifications as NotificationsIcon, Security as SecurityIcon, Language as LanguageIcon } from "@mui/icons-material";
import { useSnackbar } from "@codejq/react-simple-snackbar";
import * as api from "../../api";
import { SNACKBAR_DURATION_MS, getSnackbarColor } from "../../utils/snackbar";
const ClientPortalSettings = () => {
  const {
    t,
    i18n
  } = useTranslation("crm");
  const [openSnackbar] = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    notifications: {
      email: true,
      sms: false,
      push: false
    },
    language: 'en',
    timezone: 'UTC'
  });
  useEffect(() => {
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const loadSettings = async () => {
    try {
      setLoading(true);
      const profile = localStorage.getItem('profile');
      if (profile) {
        const userData = JSON.parse(profile);
        if (userData.token) {
          api.updateAuthToken(userData.token);
        }
      }
      const {
        data
      } = await api.fetchClientPortalSettings();
      if (data.data && data.data.settings) {
        setSettings({
          notifications: {
            email: data.data.settings.notifications?.email ?? true,
            sms: data.data.settings.notifications?.sms ?? false,
            push: data.data.settings.notifications?.push ?? false
          },
          language: data.data.settings.language || 'en',
          timezone: data.data.settings.timezone || 'UTC'
        });
      }
    } catch (error) {
      console.error("Error loading settings:", error);
      // Don't show error snackbar, just keep default settings
    } finally {
      setLoading(false);
    }
  };
  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      await api.updateClientPortalSettings(settings);
      openSnackbar("Settings saved successfully", SNACKBAR_DURATION_MS, getSnackbarColor("Settings saved successfully"));
    } catch (error) {
      console.error("Error saving settings:", error);
      openSnackbar("Error saving settings", SNACKBAR_DURATION_MS, getSnackbarColor("Error saving settings"));
    } finally {
      setSaving(false);
    }
  };
  const handleNotificationChange = type => event => {
    setSettings({
      ...settings,
      notifications: {
        ...settings.notifications,
        [type]: event.target.checked
      }
    });
  };
  const modules = [];
  if (loading) {
    return <ClientPortalLayout modules={modules}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress size={60} />
        </Box>
      </ClientPortalLayout>;
  }
  return <ClientPortalLayout modules={modules}>
      <Box>
        <Typography variant="h4" gutterBottom sx={{
        mb: 3,
        fontWeight: 600
      }}>
          <SettingsIcon sx={{
          mr: 1,
          verticalAlign: 'middle'
        }} />
          {t('settings') || 'Settings'}
        </Typography>

        <Grid container spacing={3}>
          {/* Notification Settings */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{
                display: 'flex',
                alignItems: 'center'
              }}>
                  <NotificationsIcon sx={{
                  mr: 1
                }} />
                  {t('notification_preferences') || 'Notification Preferences'}
                </Typography>
                <Divider sx={{
                my: 2
              }} />
                
                <FormControlLabel control={<Switch checked={settings.notifications.email} onChange={handleNotificationChange('email')} />} label={t('email_notifications') || 'Email Notifications'} />
                
                <FormControlLabel control={<Switch checked={settings.notifications.sms} onChange={handleNotificationChange('sms')} />} label={t('sms_notifications') || 'SMS Notifications'} sx={{
                display: 'block',
                mt: 1
              }} />
                
                <FormControlLabel control={<Switch checked={settings.notifications.push} onChange={handleNotificationChange('push')} />} label={t('push_notifications') || 'Push Notifications'} sx={{
                display: 'block',
                mt: 1
              }} />

                <Alert severity="info" sx={{
                mt: 2
              }}>
                  {t('notification_settings_info') || 'You will receive important updates and notifications through your preferred channels.'}
                </Alert>
              </CardContent>
            </Card>
          </Grid>

          {/* Language & Regional Settings */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{
                display: 'flex',
                alignItems: 'center'
              }}>
                  <LanguageIcon sx={{
                  mr: 1
                }} />
                  {t('language_regional') || 'Language & Regional'}
                </Typography>
                <Divider sx={{
                my: 2
              }} />
                
                <TextField select fullWidth label={t('language') || 'Language'} value={settings.language} onChange={e => setSettings({
                ...settings,
                language: e.target.value
              })} SelectProps={{
                native: true
              }} sx={{
                mb: 2
              }}>
                  <option value="en">{t("English")}</option>
                  <option value="ar">{t("Arabic")}</option>
                  <option value="fr">{t("French")}</option>
                  <option value="es">{t("Spanish")}</option>
                </TextField>

                <TextField select fullWidth label={t('timezone') || 'Timezone'} value={settings.timezone} onChange={e => setSettings({
                ...settings,
                timezone: e.target.value
              })} SelectProps={{
                native: true
              }}>
                  <option value="UTC">{t("UTC")}</option>
                  <option value="America/New_York">{t("Eastern Time (ET)")}</option>
                  <option value="America/Chicago">{t("Central Time (CT)")}</option>
                  <option value="America/Los_Angeles">{t("Pacific Time (PT)")}</option>
                  <option value="Europe/London">{t("London (GMT)")}</option>
                  <option value="Europe/Paris">{t("Paris (CET)")}</option>
                  <option value="Asia/Dubai">{t("Dubai (GST)")}</option>
                </TextField>
              </CardContent>
            </Card>
          </Grid>

          {/* Security Settings */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{
                display: 'flex',
                alignItems: 'center'
              }}>
                  <SecurityIcon sx={{
                  mr: 1
                }} />
                  {t('security') || 'Security'}
                </Typography>
                <Divider sx={{
                my: 2
              }} />
                
                <Alert severity="info" sx={{
                mb: 2
              }}>
                  {t('security_info') || 'To change your password, please visit the Profile page.'}
                </Alert>

                <Box>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    {t('two_factor_auth') || 'Two-Factor Authentication'}
                  </Typography>
                  <Typography variant="body2" sx={{
                  mb: 2
                }}>
                    {t('two_factor_status') || 'Status: Not Enabled'}
                  </Typography>
                  <Button variant="outlined" size="small" disabled>
                    {t('enable_2fa') || 'Enable 2FA'} ({t('coming_soon') || 'Coming Soon'})
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Save Button */}
          <Grid item xs={12}>
            <Paper sx={{
            p: 3,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2
          }}>
              <Button variant="outlined" onClick={loadSettings} disabled={saving}>
                {t('reset') || 'Reset'}
              </Button>
              <Button variant="contained" onClick={handleSaveSettings} disabled={saving}>
                {saving ? <CircularProgress size={24} /> : t('save_settings') || 'Save Settings'}
              </Button>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </ClientPortalLayout>;
};
export default ClientPortalSettings;
